import { getCurrentToken } from '../utils/auth';
import { v4 as uuid } from "uuid";

type SoundAIRequest = {
    text: string;
    duration_seconds?: number;
}

interface generateSoundProps {
    req: SoundAIRequest;
    config: any;
}

type SoundAIResult = { 
    url: string;
    duration?: number;
    requestId?: string;
}

export const generateSound = async ({ req, config }: generateSoundProps): Promise<SoundAIResult> => {
    const startTime = Date.now();
    return new Promise(async (resolve, reject) => {
        if (!config.SOUND_AI_MODEL) {
            console.error('No model specified for sound generation');
            reject(new Error('Missing model for sound generation'));
            return;
        }

        console.log('Starting sound generation with:', req);

        const id = uuid();
        let timer: ReturnType<typeof setTimeout>;
        let isRequestCancelled = false;
        // Create an AbortController for the fetch request
        const controller = new AbortController();
        const signal = controller.signal;

        const cleanup = () => {
            if (timer) {
                clearTimeout(timer);
            }
            isRequestCancelled = true;
            // Abort the fetch request if it's still in progress
            controller.abort();
        };

        try {
            const timeoutDuration = config.SOUND_AI_TIMEOUT;
            timer = setTimeout(() => {
                console.error('Sound generation timed out after', timeoutDuration, 'ms');
                cleanup();
                reject(new Error('Sound generation timed out'));
            }, timeoutDuration);

            // Get auth token for Supabase
            const authToken = await getCurrentToken();

            console.log('Calling Supabase Edge Function as a proxy to fal.ai');

            // Call the Supabase Edge Function as a proxy to fal.ai
            const response = await fetch(`${process.env.REACT_APP_SUPABASE_URL}/functions/v1/generate-image`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${authToken}`
                },
                body: JSON.stringify({
                    model: config.SOUND_AI_MODEL,
                    text: req.text,
                    ...(req.duration_seconds && { duration_seconds: req.duration_seconds })
                }),
                signal // Add the abort signal to the fetch request
            });

            // If the request was cancelled during fetch, don't proceed
            if (isRequestCancelled) {
                console.log('Request was cancelled, ignoring response');
                return;
            }

            console.log('Received response from Supabase Edge Function:', response);

            if (!response.ok) {
                const errorText = await response.text();
                throw new Error(`HTTP error! Status: ${response.status}, Details: ${errorText}`);
            }

            const result = await response.json();

            // If the request was cancelled while parsing JSON, don't proceed
            if (isRequestCancelled) {
                console.log('Request was cancelled after receiving response, ignoring result');
                return;
            }

            console.log('Received result from AI service:', result);

            if (result.audio && result.audio.url) {
                cleanup();
                const endTime = Date.now();
                const duration = endTime - startTime;
                console.log(`Sound generation completed in ${duration}ms`);
                
                resolve({ 
                    url: result.audio.url,
                    duration: result.duration || null,
                    requestId: result.requestId || id
                });
            } else {
                console.warn('Received invalid result structure:', result);
                cleanup();
                reject(new Error('Invalid result structure received from AI service'));
            }
        } catch (error) {
            console.error('Error during sound generation:', error);
            cleanup();
            reject(error);
        }
    });
};
